import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { EVIDENCE_FIELDS, evidenceKey, type EvidenceFieldConfig } from '../../constants/evidenceFields';
import type { ObservationMedia } from '../../types/observationMedia';
import {
  confirmUpload, getObservationMedia, removeObservationMedia, requestUploadUrl, uploadToBlob,
} from '../../services/observationMediaService';

/**
 * Field-level evidence photos for one test page. Loads the test's
 * field-scoped ObservationMedia once and shares it with every
 * FieldEvidenceButton on the page, plus the "evidence missing" nag.
 */
export interface FieldEvidenceContextValue {
  testTypeId: string;
  fields: EvidenceFieldConfig[];
  loaded: boolean;
  canEdit: boolean;
  itemsFor: (fieldKey: string) => ObservationMedia[];
  /** Required fields with no completed evidence photo yet. */
  missingRequired: EvidenceFieldConfig[];
  upload: (fieldKey: string, file: File, onProgress?: (pct: number) => void) => Promise<void>;
  remove: (item: ObservationMedia) => Promise<void>;
}

const FieldEvidenceContext = createContext<FieldEvidenceContextValue | null>(null);

/** Null outside a provider — shared sections degrade to no evidence buttons. */
export const useFieldEvidenceOptional = (): FieldEvidenceContextValue | null =>
  useContext(FieldEvidenceContext);

interface ProviderProps {
  workOrderDetailKey: string;
  /** Formula-Registry-style id ('rockUcs', …) — must exist in EVIDENCE_FIELDS. */
  testTypeId: string;
  /** Force read-only regardless of role. */
  readOnly?: boolean;
  /**
   * Restrict the config to these fieldKeys (e.g. BTS with 1 specimen drops
   * the specimen-2 fields). Omit for the full configured list.
   */
  enabledFieldKeys?: string[];
  /** Lets the host page mirror the nag count (tab badge) without consuming the context. */
  onMissingCountChange?: (count: number) => void;
  children: ReactNode;
}

export const FieldEvidenceProvider = ({
  workOrderDetailKey, testTypeId, readOnly = false, enabledFieldKeys, onMissingCountChange, children,
}: ProviderProps) => {
  const { canEditTestData } = usePermissions();
  const enabledKey = enabledFieldKeys?.join('|');
  const fields = useMemo(() => {
    const all = EVIDENCE_FIELDS[testTypeId] ?? [];
    const enabled = enabledKey === undefined ? null : new Set(enabledKey === '' ? [] : enabledKey.split('|'));
    return enabled ? all.filter((f) => enabled.has(f.fieldKey)) : all;
  }, [testTypeId, enabledKey]);
  const [items, setItems] = useState<ObservationMedia[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    getObservationMedia(workOrderDetailKey)
      .then((data) => {
        if (cancelled) return;
        setItems(data.filter((m) => m.UploadStatus === 'complete' && !!m.FieldKey));
      })
      .catch(() => { /* non-critical — buttons show as empty */ })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [workOrderDetailKey]);

  const itemsFor = useCallback(
    (fieldKey: string) => items.filter((m) => m.FieldKey === evidenceKey(testTypeId, fieldKey)),
    [items, testTypeId],
  );

  const missingRequired = useMemo(
    () => fields.filter((f) => f.required
      && !items.some((m) => m.FieldKey === evidenceKey(testTypeId, f.fieldKey))),
    [fields, items, testTypeId],
  );

  useEffect(() => {
    onMissingCountChange?.(loaded ? missingRequired.length : 0);
  }, [loaded, missingRequired.length, onMissingCountChange]);

  const upload = useCallback(async (fieldKey: string, file: File, onProgress?: (pct: number) => void) => {
    const { observationMediaKey, uploadUrl } = await requestUploadUrl(workOrderDetailKey, {
      FileName: file.name,
      FileSize: file.size,
      MimeType: file.type || 'application/octet-stream',
      FieldKey: evidenceKey(testTypeId, fieldKey),
    });
    await uploadToBlob(uploadUrl, file, file.type, onProgress);
    const confirmed = await confirmUpload(observationMediaKey);
    setItems((prev) => [confirmed, ...prev]);
  }, [workOrderDetailKey, testTypeId]);

  const remove = useCallback(async (item: ObservationMedia) => {
    await removeObservationMedia(item.ObservationMediaKey);
    setItems((prev) => prev.filter((m) => m.ObservationMediaKey !== item.ObservationMediaKey));
  }, []);

  const value = useMemo<FieldEvidenceContextValue>(() => ({
    testTypeId,
    fields,
    loaded,
    canEdit: canEditTestData && !readOnly,
    itemsFor,
    missingRequired,
    upload,
    remove,
  }), [testTypeId, fields, loaded, canEditTestData, readOnly, itemsFor, missingRequired, upload, remove]);

  return (
    <FieldEvidenceContext.Provider value={value}>
      {children}
    </FieldEvidenceContext.Provider>
  );
};
